<script setup>
import { ref, computed, onMounted } from "vue";
import axios from "axios";

const props = defineProps(["apiDraw", "tenantId"]);
const operatorsList = ref(null);
const search = ref("");
const categories = computed(() => operatorsList.value?.categories);

const fetchOperators = async () => {
  try {
    const response = await axios.get(
      props.apiDraw + "/" + props.tenantId + "/draw/categories",
      { withCredentials: true },
    );
    operatorsList.value = response.data;
  } catch (error) {
    console.error(error);
  }
};
onMounted(fetchOperators);

// const fetchOperatorsFromJson = async () => {
//   try {
//     const response = await axios.get(
//       new URL("../../scripts/categories.json", import.meta.url).href,
//     );
//     operatorsList.value = response.data;
//   } catch (error) {
//     console.error(error);
//   }
// };
// onMounted(fetchOperatorsFromJson);

function onDragStart(event, category, subCategory, item) {
  let newCategory = { ...category }; // Creates a copy of the original object
  delete newCategory.subcategories; // Removes subcategories

  const operator = {
    category: newCategory,
    subCategory: subCategory,
    item: item,
  };

  if (event.dataTransfer) {
    event.dataTransfer.setData("application/json", JSON.stringify(operator));
    event.dataTransfer.effectAllowed = "move";
  }
}

const filteredItems = computed(() => {
  if (!search.value || !categories.value) {
    return categories.value;
  }

  return categories.value
    .map((category) => {
      const subcategories = category.subcategories
        .map((subcategory) => {
          const items = subcategory.items.filter((item) =>
            item.name.toLowerCase().includes(search.value.toLowerCase()),
          );

          if (items.length > 0) {
            return { ...subcategory, items };
          }
        })
        .filter(Boolean);

      if (subcategories.length > 0) {
        return { ...category, subcategories };
      }
    })
    .filter(Boolean);
});

//Open operator help page
function openHelp(url) {
  window.open(url, "_blank");
}

const emit = defineEmits(["showSidebar"]);
let drawerNav = ref(true);
function manageSidebar() {
  drawerNav.value = !drawerNav.value;
  drawerNav.value
    ? emit("showSidebar", "sidebar")
    : emit("showSidebar", "no-sidebar");
}
</script>

<template>
  <v-app>
    <v-sheet
      color="secondary"
      height="100%"
      width="28px"
      class="drawerSheet bg-secondary"
      position="absolute"
      elevation="0"
    >
      <v-btn
        class="drawerBtn open text--white"
        color="secondary"
        density="compact"
        icon="mdi-chevron-right"
        flat
        position="absolute"
        @click.stop="manageSidebar()"
      ></v-btn>
    </v-sheet>
    <v-navigation-drawer
      id="operator-drawer"
      :temporary="!drawerNav"
      :permanent="drawerNav"
      width="320"
      v-model="drawerNav"
      :scrim="false"
      elevation="1"
    >
      <v-btn
        class="drawerBtn close"
        density="compact"
        color="white"
        icon="mdi-chevron-left"
        flat
        @click.stop="manageSidebar()"
      ></v-btn>
      <template v-slot:prepend>
        <v-card class="mt-6 mb-3 mx-auto ml-4" width="270px" :flat="true">
          <v-text-field
            v-model="search"
            density="compact"
            variant="outlined"
            :label="$t('labels.search')"
            prepend-inner-icon="mdi-magnify"
            single-line
            hide-details
            clearable
          ></v-text-field>
        </v-card>
      </template>

      <div class="nodes text-center">
        <!-- Op Categories -->
        <div v-if="filteredItems?.length > 0">
          <v-list v-for="cat in filteredItems" :key="cat.id">
            <template v-if="cat.opCategoryId != 'dataSources'">
              <v-list-subheader
                class="justify-center"
              >
                {{ cat.name }}
              </v-list-subheader>
              <v-divider v-if="cat.id != 'inputNode'"></v-divider>
              <v-list-item v-for="sub in cat.subcategories" :key="sub.id">
                <!-- Op SubCategories -->
                <p class="text-subtitle-2 text-left pl-2 text-primary">
                  {{ sub.name }}
                </p>
                <!-- Operators -->
                <v-chip
                  v-for="item in sub.items"
                  :key="item.id"
                  class="my-1 pointerAspect"
                  :prepend-icon="`${item.icon}`"
                  :color="cat.color"
                  label
                  size="small"
                  variant="elevated"
                  :draggable="true"
                  @dragstart="onDragStart($event, cat, [sub.id, sub.name], item)"
                >
                  {{ item.name }}
                  <v-btn
                    density="compact"
                    size="x-small"
                    icon="mdi-help-circle-outline"
                    flat
                    variant="plain"
                    @click="openHelp(item.doc)"
                  >
                  </v-btn>
                </v-chip>
              </v-list-item>
            </template>

            <!-- If Datasources -->
            <template v-else>
              <v-list-subheader
                v-if="cat.id != 'inputNode'"
                class="justify-center"
              >
                {{ cat.name }}
              </v-list-subheader>
              <v-divider></v-divider>
              <v-expansion-panels
                elevation="0"
                :tile="true"
                multiple
                variant="accordion"
                :static="true"
              >
                <v-expansion-panel
                  v-for="sub in cat.subcategories"
                  :key="sub.id"
                >
                  <!-- Op SubCategories -->
                  <v-expansion-panel-title class="py-2 px-4">
                    <p class="text-subtitle-2 text-left pl-2 text-primary">
                      {{ sub.name }}
                    </p>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text class="pa-0">
                    <!-- Operators -->
                    <v-chip
                      v-for="item in sub.items"
                      :key="item.id"
                      class="my-1 pointerAspect"
                      :prepend-icon="`${item.icon}`"
                      :color="cat.color"
                      label
                      size="small"
                      variant="elevated"
                      :draggable="true"
                      @dragstart="onDragStart($event, cat, [sub.id, sub.name], item)"
                    >
                      {{ item.name }}
                      <v-btn
                        density="compact"
                        size="x-small"
                        icon="mdi-help-circle-outline"
                        flat
                        variant="plain"
                        @click="openHelp(item.doc)"
                      >
                      </v-btn>
                    </v-chip>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
              <v-divider v-if="cat.id != 'inputNode'"></v-divider>
            </template>
          </v-list>
        </div>
        <div v-else>{{ $t("strings.no-operators") }}</div>
      </div>
    </v-navigation-drawer>
  </v-app>
</template>
<style scoped>
#operator-drawer {
  top: 63px !important;
  max-height: calc(100% - 104px);
}

.v-chip.v-chip--density-default {
  width: 270px !important;
}

.v-chip.v-chip--density-default :deep(.v-chip__content) {
  display: inline-flex;
  align-items: center;
  justify-content: space-between !important;
  width: 100% !important;
}

.pointerAspect {
  cursor: pointer;
}

.drawerBtn {
  position: absolute;
  top: 16px;
  right: -10px;
  border: 1px solid rgba(0, 0, 0, 0.12);
}

#operator-drawer.v-navigation-drawer--active .drawerBtn {
  right: -10px;
}

.drawerBtn.open {
  border: 0px;
}

.drawerBtn.close {
  border-left: none;
  border-top: none;
  border-bottom: none;
  width: 28px !important;
  padding: 0px;
  border-width: 2px;
  right: 0px;
}

.drawerSheet {
  left: -14px;
  top: 63px;
  position: absolute;
  opacity: 0.7;
  z-index: 1;
  max-height: calc(100% - 104px);
}

.nodes :deep(.v-expansion-panel-text__wrapper) {
  padding: 0px !important;
}
</style>
